import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Shield } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="max-w-[800px] mx-auto px-4 lg:px-6 py-8 space-y-6" data-testid="privacy-policy">
      <Link to="/">
        <Button variant="ghost" size="sm" className="font-body text-xs text-ink/60 rounded-[4px]">
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
        </Button>
      </Link>

      <div className="flex items-center gap-3">
        <Shield className="w-6 h-6 text-brand-orange" strokeWidth={1.5} />
        <h1 className="font-display text-3xl font-extrabold text-ink">Privacy Policy</h1>
      </div>
      <p className="font-body text-xs text-ink/40">Last updated: July 2026 · Nexintell Limited</p>

      <div className="solid-card p-6 space-y-5 font-body text-sm text-ink/70 leading-relaxed">
        <section>
          <h2 className="font-display text-lg font-extrabold text-ink mb-2">1. Introduction</h2>
          <p>Nexintell Limited (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) operates <strong>Adoptic</strong> — the Tees Valley Digital Register — a business digital-inclusion intelligence platform covering the five constituent unitary authorities of the Tees Valley Combined Authority (Stockton-on-Tees, Middlesbrough, Redcar &amp; Cleveland, Hartlepool, Darlington). This Privacy Policy explains how we collect, use, and protect information when you use our platform.</p>
        </section>

        <section>
          <h2 className="font-display text-lg font-extrabold text-ink mb-2">2. Data We Process</h2>
          <p className="mb-2"><strong className="text-ink">Business Data:</strong> We process publicly available business information sourced from:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Companies House (under Open Government Licence v3.0)</li>
            <li>Office for National Statistics (ONS) Inter-Departmental Business Register (IDBR)</li>
            <li>Ofcom Connected Nations (Fixed + Mobile, Spring 2026 release, January 2026 snapshot)</li>
            <li>MHCLG English Indices of Deprivation 2025 (LSOA-level; 2019 baseline retained for historical trend analysis)</li>
            <li>ONS Open Geography Portal (Local Authority Districts + Wards + LSOAs boundary polygons)</li>
            <li>Google Places API — used offline for one-time BDAF signal verification (business name, rating, review count, verified status)</li>
            <li>Publicly observable digital presence (websites, Google Business profiles, directory listings, social media, review platforms)</li>
          </ul>
          <p className="mt-2"><strong className="text-ink">User Account Data:</strong> When you register, we collect your name, email address, and organisation. LA-scoped accounts additionally record the Local Authority to which access is restricted. This data is stored securely and used solely for platform access, RBAC enforcement, and audit logging.</p>
        </section>

        <section>
          <h2 className="font-display text-lg font-extrabold text-ink mb-2">3. No Personal Data Collection</h2>
          <p>This platform does not collect, store, or process personal data of individuals beyond authenticated user credentials. All business data relates to registered legal entities and their publicly observable digital presence. No individual consumer data is processed.</p>
        </section>

        <section>
          <h2 className="font-display text-lg font-extrabold text-ink mb-2">4. Data Security</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>All communications are encrypted via HTTPS/TLS</li>
            <li>Passwords are hashed using bcrypt with per-user salt</li>
            <li>JWT tokens expire after 1 hour with secure refresh mechanism</li>
            <li>Role-based access control (Public, Institutional, LA User, Admin) with Local Authority scoping enforced at the query level</li>
            <li>Brute force protection with account lockout after 5 failed attempts</li>
            <li>Comprehensive audit logging of all administrative actions, role changes, and user invitations</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-lg font-extrabold text-ink mb-2">5. Data Sharing</h2>
          <p>We do not sell, trade, or otherwise transfer your information to third parties. Business data displayed on this platform is derived from publicly available sources and is presented for research, commissioning, and policy analysis purposes by the Tees Valley Combined Authority, its constituent councils, and authorised delivery partners.</p>
        </section>

        <section>
          <h2 className="font-display text-lg font-extrabold text-ink mb-2">6. Your Rights</h2>
          <p>You have the right to access, correct, or delete your account data. For data subject requests, contact us at contact@nexintell.co.</p>
        </section>

        <section>
          <h2 className="font-display text-lg font-extrabold text-ink mb-2">7. Disclaimer and Limitation of Liability</h2>
          <p>This platform is provided for informational purposes only. BDAF (Business Digital Adoption Framework) scores and related digital-inclusion assessments are point-in-time evaluations based on a reconciled probabilistic model cross-verified against Google Places. Individual business status may change at any time.</p>
          <p className="mt-2 font-medium text-status-red/80">Nexintell Limited makes no representations or warranties about the completeness, accuracy, reliability, or availability of the information. In no event will Nexintell Limited be liable for any loss or damage arising from use of this platform or its reports.</p>
          <p className="mt-2">This platform should not be used as the sole basis for any investment, business, or policy decisions. Users are advised to verify all data independently.</p>
        </section>

        <section>
          <h2 className="font-display text-lg font-extrabold text-ink mb-2">8. Contact</h2>
          <p>Nexintell Limited<br />Email: contact@nexintell.co<br />Website: www.nexintell.co</p>
        </section>
      </div>

      <p className="font-body text-xs text-ink/30 text-center">
        Adoptic · Tees Valley Digital Register &nbsp;·&nbsp; &copy; {new Date().getFullYear()} Nexintell Limited. All rights reserved.
      </p>
    </div>
  );
}
